// Another person's profile, as a floating card near where they were clicked (the Mac's popover;
// ios/Sources/Views/UserProfileView.swift + Android's UserProfileScreen for position and local
// time). Store first: the card paints from the `users`/`user_status` rows the moment it opens;
// the person is then fetched once (§2 — position and timezone ride only on the full user) and the
// fresh row lands in the store for every other screen too. Resolved by id OR username (an
// @mention knows only the handle).
//
// Their LOCAL time follows the zone THEY selected (automatic vs manual, shared/API.md §2) and
// advances once per minute, on the minute, while the card is open. A deactivated account
// (§2.4) explains itself instead of offering a message.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import type { ProfileExtras } from '../../../data/features/people';
import { userDisplayName } from '../../../core/channelListDerivation';
import { CustomStatusCodec } from '../../../core/customStatus';
import { EmojiCharacters } from '../../../core/emojiCharacters';
import { MessageTimestamp } from '../../../core/messageTimestamp';
import type { UserRecord } from '../../../core/records';
import { dateFormatter, isSameDay, localTimeZone } from '../../../core/time';
import { UserProfilePresentation } from '../../../core/userProfilePresentation';
import { Icon } from '../../../design/icons';
import { locale, t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { useLive } from '../../hooks';
import { Button } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import './people.css';

const MARGIN = 16;
const GAP = 10;

/** A clock that ticks ON the minute, so "14:05" turns into "14:06" when the wall clock does. */
function useWallMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const first = setTimeout(
      () => {
        setNow(Date.now());
        interval = setInterval(() => setNow(Date.now()), 60_000);
      },
      60_000 - (Date.now() % 60_000) + 50,
    );
    return () => {
      clearTimeout(first);
      if (interval) clearInterval(interval);
    };
  }, []);
  return now;
}

/** "Europe/Madrid" → "Madrid", "America/Argentina/Buenos_Aires" → "Buenos Aires". */
function zoneCity(zone: string): string {
  const last = zone.split('/').pop() ?? zone;
  return last.replace(/_/g, ' ');
}

/** "+7 h", "−1 h", "+5:30 h": how far their clock is from the reader's, at this instant. */
function offsetFromReader(now: number, zone: string): string | null {
  const diff = MessageTimestamp.secondsFromGMT(now, zone) - MessageTimestamp.secondsFromGMT(now, localTimeZone());
  if (diff === 0) return null;
  const sign = diff > 0 ? '+' : '−';
  const minutes = Math.abs(diff) / 60;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''} h`;
}

function untilLabel(expiresAt: number, now: number): string {
  const sameDay = isSameDay(expiresAt, now, localTimeZone());
  const options: Intl.DateTimeFormatOptions = sameDay
    ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
    : { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
  return tf('profile.customStatus.until', dateFormatter(locale(), options, localTimeZone()).format(new Date(expiresAt)));
}

export function UserProfileCard({ userId, username, anchor, onClose }: { userId?: string; username?: string; anchor?: { x: number; y: number }; onClose(): void }) {
  const shell = useShell();
  const target = { userId, username };
  const { value: local } = useLive(() => data.call('userProfileLocal', target), ['users', 'userStatuses'], [userId ?? '', username ?? '']);
  const [fetched, setFetched] = useState<{ user: UserRecord; extras: ProfileExtras } | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const now = useWallMinute();
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    let live = true;
    setFetched(null);
    setFetchFailed(false);
    void data.call('userProfileFetch', target).then(
      (r) => {
        if (!live) return;
        if ('error' in r) setFetchFailed(true);
        else setFetched(r);
      },
      () => live && setFetchFailed(true),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, username]);

  // Click outside or Escape closes; the opening click must not count as "outside".
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const id = setTimeout(() => window.addEventListener('mousedown', outside));
    window.addEventListener('keydown', esc, true);
    return () => {
      clearTimeout(id);
      window.removeEventListener('mousedown', outside);
      window.removeEventListener('keydown', esc, true);
    };
  }, [onClose]);

  const user = local?.user ?? fetched?.user ?? null;
  const extras = fetched?.extras ?? local?.extras ?? null;
  const status = local?.status ?? null;

  // Near the click, never off screen: to the right and below the anchor, flipped to the other
  // side of it when that edge is too close, clamped to the viewport as the last resort.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left: number;
    let top: number;
    if (anchor) {
      left = anchor.x + GAP + w <= vw - MARGIN ? anchor.x + GAP : anchor.x - GAP - w;
      top = anchor.y + h <= vh - MARGIN ? anchor.y : anchor.y - h;
    } else {
      left = (vw - w) / 2;
      top = (vh - h) / 2;
    }
    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
    setPosition((p) => (p && Math.abs(p.left - left) < 1 && Math.abs(p.top - top) < 1 ? p : { left, top }));
  });

  const name = user ? userDisplayName(user) : '';
  const deactivated = user ? user.deleteAt !== 0 : false;
  const customStatus = user ? CustomStatusCodec.fromPropsJSON(user.customStatusJSON) : null;
  const activeStatus = customStatus && CustomStatusCodec.isActive(customStatus, now) ? customStatus : null;
  const zone = UserProfilePresentation.selectedTimeZone(extras?.timezone ?? null);
  const time = zone
    ? UserProfilePresentation.localTime({
        automaticTimezone: zone,
        manualTimezone: null,
        useAutomaticTimezone: true,
        nowMillis: now,
        use24HourClock: true,
        locale: locale(),
      })
    : null;
  const offset = zone ? offsetFromReader(now, zone) : null;
  const isMe = user?.id === shell.me.id;

  const copy = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText('@' + user.username);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      shell.toast(t('common.unexpectedError'), 'error');
    }
  };

  const message = () => {
    if (!user) return;
    onClose();
    shell.openDirectMessage(user.id);
  };

  return (
    <div
      ref={ref}
      className="profile-card"
      role="dialog"
      aria-label={name || t('profile.title')}
      style={{ left: position?.left ?? -9999, top: position?.top ?? -9999, visibility: position ? 'visible' : 'hidden' }}
    >
      {user ? (
        <>
          <div className="profile-card-head">
            <Avatar userId={user.id} name={name} size={72} version={user.lastPictureUpdate} presence={deactivated ? null : status} />
            <h2 className="profile-card-name">
              {name}
              {isMe ? <span className="people-badge">{t('web.people.profile.you')}</span> : null}
              {deactivated ? <span className="people-badge">{t('web.people.profile.deactivatedBadge')}</span> : null}
            </h2>
            <div className="profile-card-handle">@{user.username}</div>
            {status && !deactivated ? (
              <div className="profile-card-presence">
                <span className={`dot ${status}`} />
                {UserProfilePresentation.statusLabel(status)}
              </div>
            ) : null}
            {activeStatus ? (
              <div className="profile-card-status" title={activeStatus.text}>
                <span aria-hidden="true">{EmojiCharacters.character(activeStatus.emoji) ?? '🙂'}</span>
                {activeStatus.text ? <span className="profile-card-status-text">{activeStatus.text}</span> : null}
                {activeStatus.expiresAt ? <span className="profile-card-status-until">· {untilLabel(activeStatus.expiresAt, now)}</span> : null}
              </div>
            ) : null}
          </div>
          {extras?.position || user.nickname || time || user.email ? (
            <dl className="profile-card-facts">
              {extras?.position ? (
                <div className="profile-card-fact">
                  <dt>{t('profile.field.position')}</dt>
                  <dd>{extras.position}</dd>
                </div>
              ) : null}
              {user.nickname ? (
                <div className="profile-card-fact">
                  <dt>{t('profile.field.nickname')}</dt>
                  <dd>{user.nickname}</dd>
                </div>
              ) : null}
              {time && zone ? (
                <div className="profile-card-fact">
                  <dt>{t('web.people.profile.localTime')}</dt>
                  <dd>
                    {time} <span className="zone">· {zoneCity(zone)}{offset ? ` (${offset})` : ''}</span>
                  </dd>
                </div>
              ) : null}
              {user.email ? (
                <div className="profile-card-fact">
                  <dt>{t('profile.field.email')}</dt>
                  <dd className="single-line" title={user.email}>
                    {user.email}
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
          {deactivated ? <div className="profile-card-notice">{t('web.people.profile.deactivated')}</div> : null}
          <div className="profile-card-actions">
            {!deactivated ? (
              <Button kind="primary" icon="send" onClick={message}>
                {t('profile.sendDirectMessage')}
              </Button>
            ) : null}
            <button
              type="button"
              className={`btn btn-secondary profile-card-copy${deactivated ? ' wide' : ''}`}
              onClick={() => void copy()}
              title={t('web.people.profile.copyUsername')}
              aria-label={t('web.people.profile.copyUsername')}
            >
              <Icon name={copied ? 'check' : 'copy'} size={15} />
              {deactivated || copied ? <span>{copied ? t('web.people.profile.copied') : t('web.people.profile.copyUsername')}</span> : null}
            </button>
          </div>
        </>
      ) : fetchFailed ? (
        <div className="profile-card-error">{t('profile.error.load')}</div>
      ) : (
        <div className="profile-card-head" aria-busy="true">
          <Avatar userId={userId ?? username ?? ''} name={username ?? ''} size={72} />
          <div className="profile-card-skeleton" />
          <div className="profile-card-skeleton" style={{ width: 90 }} />
        </div>
      )}
    </div>
  );
}
